import React from 'react';
import * as Sentry from '@sentry/react';
import { TbAlertCircle } from 'react-icons/tb';
import * as Styled from './style';
import { debug } from '../../services/logger';

/**
 * ErrorBoundary is a reusable React component for catching errors in child components.
 *
 * @component
 * @example
 * return (
 *   <ErrorBoundary componentName="UserProfile">
 *     <UserProfile />
 *   </ErrorBoundary>
 * )
 *
 * @typedef {Object} ErrorBoundaryProps
 * @property {string} componentName - The name of the component being wrapped. Used for logging and error reporting.
 * @property {React.ReactNode} children - Child components to render.
 * @property {React.ReactNode} [fallback] - Optional fallback UI to render on error.
 * @property {string} [title] - User-facing error heading.
 * @property {string} [description] - User-facing guidance.
 */

interface ErrorBoundaryProps {
  componentName: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  title?: string;
  description?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { componentName } = this.props;
    const namedError = new Error(`[Fatal]: ${error.message}`);
    namedError.stack = error.stack;

    debug('ErrorBoundary', `fatal render error at ${componentName}`, { error, errorInfo });

    Sentry.captureException(namedError, {
      tags: { component: componentName },
      extra: {
        componentStack: errorInfo.componentStack,
        ...errorInfo
      }
    });
  }

  render(): React.ReactNode {
    const { hasError } = this.state;
    const {
      fallback,
      componentName,
      children,
      title = "We couldn't display this section.",
      description = 'Please try again. If the problem continues, contact support.'
    } = this.props;
    const idPrefix = `error-boundary-${componentName}`;

    if (hasError) {
      return fallback ? (
        fallback
      ) : (
        <Styled.ErrorContainer
          id={idPrefix}
          role="alert"
          aria-labelledby={`${idPrefix}-title`}
          aria-describedby={`${idPrefix}-description`}
        >
          <TbAlertCircle size={24} aria-hidden="true" />
          <div id={`${idPrefix}-content`}>
            <h2 id={`${idPrefix}-title`}>{title}</h2>
            <p id={`${idPrefix}-description`}>{description}</p>
          </div>
        </Styled.ErrorContainer>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
